import { doc, setDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/config';

export interface ForceRefreshData {
  timestamp: Timestamp;
  triggeredBy: string;
  reason?: string;
}

const LAST_REFRESH_KEY = 'fin_last_force_refresh_timestamp';
const SESSION_REFRESHED_KEY = 'fin_force_refresh_session';

const shouldSkipRefresh = (): boolean => {
  if (window.location.pathname.includes('/product-entry')) return true;
  try {
    const userData = localStorage.getItem('fin_user_data');
    if (userData) {
      const parsed = JSON.parse(userData);
      const roles = parsed?.roles || [];
      const hasTemplate = roles.includes('Template') || roles.includes('Template Admin');
      const hasAdmin = roles.includes('Master Admin') || roles.includes('CS Admin');
      if (hasTemplate && !hasAdmin) return true;
    }
  } catch {
    // ignore
  }
  return false;
};

/**
 * Performs a true hard refresh of the current session so the user picks up the
 * latest deployed code. Crucially it does NOT touch Firebase Auth state:
 *   - Firebase Auth lives in IndexedDB (`firebaseLocalStorageDb`) which we
 *     never delete, so the user stays logged in.
 *   - We also leave `fin_user_data` and other localStorage keys alone.
 *
 * What it actually does:
 *   1. Deletes every CacheStorage entry (the PWA app-shell + asset cache that
 *      makes plain reloads serve stale JS/HTML).
 *   2. Unregisters all service workers so the next page load installs a fresh
 *      SW pointing at the newest bundle.
 *   3. Replaces the URL with a cache-busting query param so the browser HTTP
 *      cache can't serve the old index.html either.
 */
export const hardRefreshSession = async (): Promise<void> => {
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch (err) {
    console.error('[hardRefreshSession] failed to clear CacheStorage:', err);
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (err) {
    console.error('[hardRefreshSession] failed to unregister SW:', err);
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_r', Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    // Fallback: even if URL parsing fails for some reason, reload normally so
    // the user isn't stranded.
    window.location.reload();
  }
};

class ForceRefreshService {
  private static instance: ForceRefreshService;
  private lastRefreshTimestamp: number = 0;
  private isInitialized: boolean = false;
  private initTime: number = Date.now();

  static getInstance(): ForceRefreshService {
    if (!ForceRefreshService.instance) {
      ForceRefreshService.instance = new ForceRefreshService();
    }
    return ForceRefreshService.instance;
  }

  constructor() {
    const stored = localStorage.getItem(LAST_REFRESH_KEY);
    if (stored) {
      this.lastRefreshTimestamp = parseInt(stored, 10) || 0;
    }
  }

  initializeListener(): () => void {
    const docRef = doc(db, 'system', 'force_refresh');

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as ForceRefreshData;
        const refreshTimestamp = data.timestamp.toMillis();

        if (!this.isInitialized) {
          // First snapshot after page load -- just record the current value
          this.lastRefreshTimestamp = Math.max(this.lastRefreshTimestamp, refreshTimestamp);
          localStorage.setItem(LAST_REFRESH_KEY, this.lastRefreshTimestamp.toString());
          this.isInitialized = true;
          return;
        }

        // Ignore timestamps we already handled
        if (refreshTimestamp <= this.lastRefreshTimestamp) return;

        // Guard: ignore if the app loaded less than 8 seconds ago (prevents loops
        // where the reload picks up the same write before localStorage persists)
        if (Date.now() - this.initTime < 8000) {
          this.lastRefreshTimestamp = refreshTimestamp;
          localStorage.setItem(LAST_REFRESH_KEY, refreshTimestamp.toString());
          return;
        }

        // Session guard: if we already refreshed for this exact timestamp, skip
        const sessionVal = sessionStorage.getItem(SESSION_REFRESHED_KEY);
        if (sessionVal === refreshTimestamp.toString()) return;

        // Persist before reloading so the next page load won't re-trigger
        this.lastRefreshTimestamp = refreshTimestamp;
        localStorage.setItem(LAST_REFRESH_KEY, refreshTimestamp.toString());
        sessionStorage.setItem(SESSION_REFRESHED_KEY, refreshTimestamp.toString());

        if (shouldSkipRefresh()) return;

        console.log('🔄 Force refresh triggered by:', data.triggeredBy, data.reason || '');
        this.showRefreshNotification(data.reason);
        setTimeout(() => {
          hardRefreshSession().catch((err) => {
            console.error('🔄 hardRefreshSession failed, falling back to reload:', err);
            window.location.reload();
          });
        }, 2000);
      } else {
        this.isInitialized = true;
      }
    }, (error) => {
      console.error('🔄 Force refresh listener error:', error);
      this.isInitialized = true;
    });

    return unsubscribe;
  }

  /**
   * Hard-refresh the *current* tab's session immediately (no Firestore hop).
   * Useful for a "Refresh my session" button — the user stays logged in but
   * picks up the latest deployed code on the next paint.
   */
  async hardRefreshCurrentSession(): Promise<void> {
    await hardRefreshSession();
  }

  /**
   * Trigger a force refresh for all users (admin only)
   */
  async triggerForceRefresh(triggeredBy: string, reason?: string): Promise<void> {
    try {
      const refreshData: any = {
        timestamp: Timestamp.now(),
        triggeredBy
      };

      // Only add reason if it's provided
      if (reason) {
        refreshData.reason = reason;
      }

      await setDoc(doc(db, 'system', 'force_refresh'), refreshData);
      console.log('🔄 Force refresh triggered successfully');
    } catch (error) {
      console.error('🔄 Failed to trigger force refresh:', error);
      throw error;
    }
  }

  /**
   * Show a notification to users before refreshing
   */
  private showRefreshNotification(reason?: string): void {
    // Create a toast-like notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      max-width: 300px;
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    `;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="width: 20px; height: 20px; border: 2px solid white; border-top: 2px solid transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <div>
          <div style="font-weight: 600; margin-bottom: 4px;">App Update</div>
          <div style="font-size: 12px; opacity: 0.9;">
            ${reason || 'Refreshing app for all users...'}
          </div>
        </div>
      </div>
    `;

    // Add spin animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(notification);

    // Remove notification after refresh
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  }
}

export const forceRefreshService = ForceRefreshService.getInstance();
